import {
	getAssureReductionsUrl,
	getPlantOverviewUrl,
	getProductAllocationUrl,
} from "@pages/mrv/lib/urls"
import { PermissionType } from "@shared/api/authentication/types"
import { APP_NAME_ADMIN, APP_NAME_COMMON } from "@shared/config"
import { useAuth } from "@shared/lib/auth"
import {
	imprint,
	termsAndConditions,
	privacyPolicy,
	openSourceAttribution,
} from "@shared/routes"
import { languageManagement, outbox } from "@shared/routes/admin"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

interface NavigationConfig {
	productName: string
	navigation: { label: string; url: string }[]
}

const legalPaths = [
	imprint,
	termsAndConditions,
	privacyPolicy,
	openSourceAttribution,
]

export const useNavigationConfig = (): NavigationConfig => {
	const { pathname } = useLocation()
	const { t } = useTranslation("layout")
	const { userPermissions } = useAuth()

	return useMemo(() => {
		if (pathname.startsWith("/admin")) {
			const navigation: { label: string; url: string }[] = [
				{
					label: t("admin.outbox"),
					url: outbox,
				},
				{
					label: t("admin.languageManagement"),
					url: languageManagement,
				},
				{
					label: t("admin.productAllocation"),
					url: getProductAllocationUrl(),
				},
			]

			return { productName: APP_NAME_ADMIN, navigation }
		}

		if (pathname.startsWith("/carbon-savings")) {
			const navigation: { label: string; url: string }[] = []

			if (
				userPermissions?.some(
					p =>
						p.permissionType === PermissionType.MrvAnnualAssurer ||
						p.permissionType === PermissionType.MrvCoordinator ||
						p.permissionType === PermissionType.Admin,
				)
			) {
				navigation.push({
					label: t("mrv.overview"),
					url: getPlantOverviewUrl(),
				})
			}

			if (
				userPermissions?.some(
					p =>
						p.permissionType === PermissionType.MrvDepositAssurer ||
						p.permissionType === PermissionType.MrvCoordinator,
				)
			) {
				navigation.push({
					label: t("mrv.assureSavings"),
					url: getAssureReductionsUrl(),
				})
			}

			if (
				userPermissions?.some(
					p => p.permissionType === PermissionType.Admin,
				)
			) {
				navigation.push({
					label: t("admin.outbox"),
					url: outbox,
				})
			}

			if (
				userPermissions?.some(
					p =>
						p.permissionType === PermissionType.MrvAnnualAssurer ||
						p.permissionType === PermissionType.MrvCoordinator ||
						p.permissionType === PermissionType.Admin,
				)
			) {
				navigation.push({
					label: t("mrv.productAllocation"),
					url: getProductAllocationUrl(),
				})
			}

			return { productName: APP_NAME_COMMON, navigation }
		}

		if (legalPaths.some(p => pathname.startsWith(p))) {
			return {
				productName: APP_NAME_COMMON,
				navigation: [
					{ label: t("legal.imprintTitle"), url: imprint },
					{
						label: t("legal.termsAndConditionsTitle"),
						url: termsAndConditions,
					},
					{
						label: t("legal.privacyPolicyTitle"),
						url: privacyPolicy,
					},
					{
						label: t("legal.openSourceAttributionTitle"),
						url: openSourceAttribution,
					},
				],
			}
		}

		return { productName: APP_NAME_COMMON, navigation: [] }
	}, [pathname, userPermissions, t])
}
